import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Upload, Download, CheckCircle, Clock, XCircle, ArrowLeft, AlertCircle, Clock4, Loader2, Check, Ban, Lock } from 'lucide-react';
import { documentsAPI, projectsAPI } from '../../services/api';
import useAuthStore from '../../context/authStore';
import { useDocumentApprovals } from '../../hooks/useDocumentApprovals';
import { isClientWorkerRole } from '../../utils/roles';

import BackButton from '../../components/common/BackButton';
export default function DocumentsPage() {
  const { id: projectId } = useParams();
  const { user, isInternalTeam } = useAuthStore();
  const navigate = useNavigate();
  const { canApproveDocuments, approveMutation, revisionMutation, denyMutation } = useDocumentApprovals();
  const [queueView, setQueueView] = useState('all'); // all, awaiting_review, awaiting_client, needs_revision, approved
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('25');

  const backLink = projectId ? `/projects/${projectId}` : '/dashboard';

  // facility_worker role cannot view documents - they only have training access
  if (isClientWorkerRole(user)) {
    return (
      <div className="space-y-6">
        <BackButton fallbackTo={backLink} plainClassName="inline-flex items-center gap-2 text-secondary-600 hover:text-secondary-900" />
        <div className="card p-8 text-center">
          <Lock size={48} className="mx-auto mb-4 text-secondary-400" />
          <h2 className="text-xl font-semibold text-secondary-900 mb-2">Access Restricted</h2>
          <p className="text-secondary-600">You do not have permission to view documents. Your access is limited to training-related content.</p>
        </div>
      </div>
    );
  }

  const { data, isLoading } = useQuery({
    queryKey: ['documents', projectId],
    queryFn: () => documentsAPI.getAll({ projectId }),
    select: (res) => res.data,
    enabled: !!projectId,
  });

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsAPI.getById(projectId),
    select: (res) => res.data?.project || res.project || res.data,
    enabled: !!projectId,
  });

  const isProjectCompleted = project?.status === 'completed';

  const { data: projectsData = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['client-projects'],
    queryFn: () => projectsAPI.getAll(),
    select: (res) => res.data?.projects || res.projects || [],
    enabled: !projectId,
  });

  useEffect(() => {
    if (!projectId && projectsData.length === 1) {
      navigate(`/projects/${projectsData[0]._id}/documents`);
    }
  }, [projectId, projectsData, navigate]);

  const resolveDocumentLink = (doc) => {
    if (doc.documentType === 'collaboration') {
      return `/documents/draft/${doc._id}`;
    }
    return `/documents/${doc._id}`;
  };

  const resolveSetupLink = (doc) => `/documents/draft/${doc._id}?setup=1`;

  const statusLabels = {
    draft: 'In Preparation',
    internal_review: 'In Preparation',
    shared_with_client: 'Ready for Your Review',
    client_review: 'Under Review',
    awaiting_internal_response: 'Response Sent',
    draft_approved: 'Approved',
    final_version_pending: 'Being Finalized',
    final_version_ready: 'Ready to Sign',
    revision: 'Revision Requested',
    approved: 'Approved',
    denied: 'Returned',
  };

  const statusIcons = {
    draft: Clock,
    internal_review: Clock,
    shared_with_client: Clock4,
    client_review: Clock4,
    awaiting_internal_response: AlertCircle,
    draft_approved: CheckCircle,
    final_version_pending: Clock,
    final_version_ready: CheckCircle,
    revision: AlertCircle,
    approved: CheckCircle,
    denied: XCircle,
  };

  const statusColors = {
    draft: 'text-secondary-500',
    internal_review: 'text-amber-500',
    shared_with_client: 'text-blue-500',
    client_review: 'text-blue-500',
    awaiting_internal_response: 'text-orange-500',
    draft_approved: 'text-green-500',
    final_version_pending: 'text-amber-500',
    final_version_ready: 'text-green-500',
    revision: 'text-orange-500',
    approved: 'text-green-500',
    denied: 'text-red-500',
  };

  // Queue categorization (client view)
  const awaitingReviewStatuses = ['draft', 'internal_review'];
  const awaitingClientStatuses = ['shared_with_client', 'client_review'];
  const needsRevisionStatuses = ['revision'];
  const approvedStatuses = ['approved'];

  const filteredDocuments = useMemo(() => {
    const docs = data?.documents || [];
    
    if (queueView === 'awaiting_review') {
      return docs.filter(d => awaitingReviewStatuses.includes(d.status));
    } else if (queueView === 'awaiting_client') {
      return docs.filter(d => awaitingClientStatuses.includes(d.status));
    } else if (queueView === 'needs_revision') {
      return docs.filter(d => needsRevisionStatuses.includes(d.status));
    } else if (queueView === 'approved') {
      return docs.filter(d => approvedStatuses.includes(d.status));
    }
    
    return docs;
  }, [data?.documents, queueView]);

  // Calculate status counts
  const queueCounts = useMemo(() => {
    const docs = data?.documents || [];
    return {
      all: docs.length,
      awaiting_review: docs.filter(d => awaitingReviewStatuses.includes(d.status)).length,
      awaiting_client: docs.filter(d => awaitingClientStatuses.includes(d.status)).length,
      needs_revision: docs.filter(d => needsRevisionStatuses.includes(d.status)).length,
      approved: docs.filter(d => approvedStatuses.includes(d.status)).length,
    };
  }, [data?.documents]);

  useEffect(() => {
    setPage(1);
  }, [queueView, pageSize, projectId, data?.documents?.length]);

  const pagination = useMemo(() => {
    const total = filteredDocuments.length;
    if (pageSize === 'all') {
      return { total, pages: 1, currentPage: 1, items: filteredDocuments };
    }
    const limit = Math.max(1, Number(pageSize) || 25);
    const pages = Math.max(1, Math.ceil(total / limit));
    const currentPage = Math.min(page, pages);
    const start = (currentPage - 1) * limit;
    return {
      total,
      pages,
      currentPage,
      items: filteredDocuments.slice(start, start + limit),
    };
  }, [filteredDocuments, page, pageSize]);

  if (!projectId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-secondary-900">Documents</h1>
            <p className="mt-1 text-secondary-600">Select a project to view documents</p>
          </div>
        </div>

        <div className="card p-6 max-w-xl">
          {projectsLoading ? (
            <div className="text-secondary-500">Loading projects...</div>
          ) : projectsData.length === 0 ? (
            <div className="text-secondary-500">No projects available yet.</div>
          ) : (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-secondary-700">
                Project
              </label>
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">Select a project</option>
                {projectsData.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  if (selectedProjectId) {
                    navigate(`/projects/${selectedProjectId}/documents`);
                  }
                }}
                disabled={!selectedProjectId}
                className="btn-primary disabled:opacity-50"
              >
                View Documents
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton fallbackTo={`/projects/${projectId}`} plainClassName="inline-flex items-center gap-2 text-secondary-600 hover:text-secondary-900" />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-secondary-900">Documents</h1>
          <p className="mt-1 text-secondary-600">Manage project documents and files</p>
        </div>

      </div>

      {/* Document Status Tabs */}
      <div className="card border-b border-secondary-200">
        <div className="flex overflow-x-auto gap-1 p-4">
          {[
            { id: 'all', label: 'All Documents', icon: null },
            { id: 'awaiting_review', label: 'In Review', icon: Clock },
            { id: 'awaiting_client', label: 'Your Review', icon: AlertCircle },
            { id: 'needs_revision', label: 'Need Revision', icon: Clock4 },
            { id: 'approved', label: 'Approved', icon: CheckCircle },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setQueueView(id)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                queueView === id
                  ? 'bg-primary-600 text-white'
                  : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
              }`}
            >
              {Icon && <Icon size={18} />}
              {label}
              <span className={`text-sm font-semibold ${queueView === id ? 'text-white' : 'text-secondary-600'}`}>
                {queueCounts[id]}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-200">
          <p className="text-sm text-secondary-600">Showing {pagination.items.length} of {pagination.total} documents</p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-secondary-600">Per page</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">Show all</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : filteredDocuments.length > 0 ? (
          <div className="divide-y divide-secondary-200">
            {pagination.items.map((doc) => {
              const StatusIcon = statusIcons[doc.status] || Clock;
              return (
                <div
                  key={doc._id}
                  className="group flex items-center gap-4 p-4 hover:bg-secondary-50 transition-colors"
                >
                  <Link
                    to={resolveDocumentLink(doc)}
                    className="flex-1 flex items-center gap-4 min-w-0"
                  >
                    <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-secondary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-secondary-900 truncate">{doc.name}</p>
                      <p className="text-sm text-secondary-500">
                        Version {doc.currentVersion} - {doc.category}
                      </p>
                    </div>
                    <div className={`flex items-center gap-2 ${statusColors[doc.status]} flex-shrink-0`}>
                      <StatusIcon size={16} />
                      <span className="text-sm">{statusLabels[doc.status] || doc.status.replace(/_/g, ' ')}</span>
                    </div>
                  </Link>

                  {doc.documentType === 'collaboration' && !isProjectCompleted && (
                    <Link
                      to={resolveSetupLink(doc)}
                      className="px-3 py-1.5 border border-secondary-300 text-secondary-700 rounded-lg text-sm hover:bg-secondary-100"
                    >
                      Edit Setup
                    </Link>
                  )}

                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center text-secondary-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-secondary-300" />
            <p>No documents yet</p>

          </div>
        )}
        {filteredDocuments.length > 0 && pagination.pages > 1 && pageSize !== 'all' && (
          <div className="flex items-center justify-between px-4 py-4 border-t border-secondary-200">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={pagination.currentPage === 1}
              className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-secondary-600">Page {pagination.currentPage} of {pagination.pages}</span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(pagination.pages, prev + 1))}
              disabled={pagination.currentPage === pagination.pages}
              className="px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
